import React, { useRef } from 'react';
import { Box, Typography, Button, IconButton, Select, MenuItem, TextField } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormContext } from '../context/FormContext';
import { fileToBase64 } from '../utils/fileUtils';
import { CRESTAL_BONE_OPTIONS } from './ImpactionConstants';
import ImpactionTextPreview from './ImpactionTextPreview';
import MmInput from './MmInput';
import { generateMeasurementsText } from '../utils/impactionTextGenerator';

// Helper for consistent label styling (matching ImpactionTab_Position)
const FieldLabel = ({ children }) => (
    <Typography sx={{ fontSize: '13px', mb: '5px' }}>
        {children}
    </Typography>
);

const ImpactionTab_Measurements = ({ tooth }) => {
    const { state, actions } = useFormContext();
    const toothData = state.impactionData?.[tooth] || {};
    const measurementsData = toothData.measurements || {};

    const updateData = (key, value) => {
        actions.setImpactionData({
            ...state.impactionData,
            [tooth]: {
                ...toothData,
                measurements: {
                    ...measurementsData,
                    [key]: value
                }
            }
        });
    };

    // We need to support both object and string for image data to be safe, 
    // but ideally we switch to object to support filename display.
    // For this task, I'll assume we can store the object.

    const handleImageFile = async (key, file) => {
        if (!file) return;
        try {
            const base64 = await fileToBase64(file);
            updateData(key, { base64, name: file.name, type: file.type });
        } catch (error) {
            console.error("Error converting file:", error);
        }
    };

    const handleRemoveImage = (key) => {
        updateData(key, null);
    };

    const renderImageSection = (label, key, imageValue) => {
        // imageValue could be string (old) or object (new match)
        const preview = imageValue?.base64 || (typeof imageValue === 'string' ? imageValue : null);
        const fileName = imageValue?.name || '';

        return (
            <Box sx={{ width: '350px', flexShrink: 0 }}>
                <Box sx={{ position: 'relative', mb: '15px' }}>
                    <Box
                        sx={{
                            width: '350px',
                            height: '250px',
                            border: '2px solid black', // Matching Position tab style
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.200',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        {preview && (
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveImage(key)}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    bgcolor: 'background.paper',
                                    '&:hover': { bgcolor: 'grey.100' },
                                    zIndex: 1
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}

                        {preview ? (
                            <img
                                src={preview}
                                alt={label}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {label}
                            </Typography>
                        )}
                    </Box>

                    {fileName && (
                        <Typography variant="caption" sx={{ color: 'success.main', fontSize: 10, display: 'block', mt: 0.5 }}>
                            {fileName}
                        </Typography>
                    )}
                </Box>

                <Button
                    variant={preview ? "outlined" : "contained"}
                    fullWidth
                    color={preview ? "secondary" : "primary"}
                    onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => handleImageFile(key, e.target.files[0]);
                        input.click();
                    }}
                    sx={{ mb: '15px' }}
                >
                    {preview ? 'Replace Image' : 'Select Image'}
                </Button>
            </Box>
        );
    };

    // Styles matching ImpactionTab_Position.js
    const sectionBoxStyle = { border: '1px solid #e0e0e0', borderRadius: '4px', mb: '20px', p: '20px' };
    const sectionHeaderStyle = {
        fontSize: '18px',
        fontWeight: 'bold',
        mb: '15px',
        pb: '10px'
    };
    const inputStyle = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 0,
        },
        '& .MuiOutlinedInput-input': {
            padding: '8px 14px',
            fontSize: '14px',
        }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
                Measurements ({tooth})
            </Typography>

            {/* SECTION 1: CROWN MEASUREMENTS */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>CROWN MEASUREMENTS</Typography>

                <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                    {/* Left: Image */}
                    {renderImageSection('Crown Measurement Image (Level of CEJ)', 'crownMdImage', measurementsData.crownMdImage)}

                    {/* Right: Inputs */}
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignContent: 'start' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Mesiodistal Measurement (at CEJ)</FieldLabel>
                            <Box display="flex" alignItems="center" gap={1}>
                                <MmInput
                                    value={measurementsData.crownMd}
                                    onChange={(e) => updateData('crownMd', e.target.value)}
                                    width="100%"
                                    placeholder="Enter width"
                                    sx={inputStyle}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <ImpactionTextPreview text={generateMeasurementsText(measurementsData, 'crown')} />
            </Box>

            {/* SECTION 2: CRESTAL BONE */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>CRESTAL BONE</Typography>
                <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                    {/* No Image for Crestal Bone currently, so just inputs full width or constrained? 
                        Position Tab had it as full width (maxWidth 500px). 
                        Let's keep it consistent with the "Inputs" style of other sections if possible, 
                        or just a standalone block. I'll use the grid container for consistency. */}
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignContent: 'start' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Crestal Bone Status</FieldLabel>
                            <Select
                                value={measurementsData.crestalBone || ''}
                                onChange={(e) => updateData('crestalBone', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {CRESTAL_BONE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>
                    </Box>
                </Box>
                <ImpactionTextPreview text={generateMeasurementsText(measurementsData, 'crestal')} />
            </Box>

            {/* SECTION 3: BUCCOLINGUAL BONE COVERAGE */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>BUCCOLINGUAL BONE COVERAGE</Typography>
                <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                    {/* Left: Image */}
                    {renderImageSection('Buccolingual Bone Coverage Image', 'buccolingualImage', measurementsData.buccolingualImage)}

                    {/* Right: Inputs (Empty for now per requirements, but structure ready) */}
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignContent: 'start' }}>
                        {/* Placeholder or future inputs */}
                    </Box>
                </Box>
                <ImpactionTextPreview text={generateMeasurementsText(measurementsData, 'buccolingual')} />
            </Box>

        </Box>
    );
};

export default ImpactionTab_Measurements;
